import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Hub } from 'aws-amplify/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
} from 'aws-amplify/auth';

import LoadingZone from 'components/LoadingZone';
import { fnChildrenPropType } from 'utils/customPropTypes';
import { gtag } from 'utils/common';
import {
  determineIdpFromCognitoUsername,
  isFirstTimeUser,
  getRedirectPath,
  userAuthStatusMap,
  clearTimeoutRef,
} from './helpers';

export const UserAuthContext = createContext(null);

const UserAuthManager = ({ children }) => {
  const fnNavigate = useNavigate();
  const oLocation = useLocation();
  const [bIsAuthenticating, fnSetIsAuthenticating] = useState(true);
  const [oUser, fnSetUser] = useState(null);
  const bIsFirstTimeUser = useRef(false);
  const [userAuthStatus, setUserAuthStatus] = useState(
    userAuthStatusMap.loading
  );

  // e.g. "loading", "not-signed-in", "signed-in-unmatched", "signed-in-matched"

  // Coordinate Hub sign in and custom OAuth state handling events
  const oOAuthStateReceivedRef = useRef(false);
  const oIsUserSignedInRef = useRef(false);
  const oCustomOAuthStateRef = useRef(null);
  const redirectTimeoutRef = useRef(null);

  const handleInitialAuth = useCallback(
    (oCognitoUser) => {
      if (!bIsFirstTimeUser.current) {
        fnSetUser(oCognitoUser);
        // Don't set to signedInMatched if user is still in sign-up flow
        // This prevents errors on the /sign-up/finalize-account (terms of service) page
        if (!oLocation.pathname.startsWith('/sign-up')) {
          setUserAuthStatus(userAuthStatusMap.signedInMatched);
        } else {
          setUserAuthStatus(userAuthStatusMap.signedInUnmatched);
        }
      }
    },
    [oLocation.pathname]
  );

  const fnHandlePostSignInRedirect = useCallback(() => {
    // Clear any existing timeout
    clearTimeoutRef(redirectTimeoutRef);

    if (oIsUserSignedInRef.current && oOAuthStateReceivedRef.current) {
      if (bIsFirstTimeUser.current) {
        setUserAuthStatus(userAuthStatusMap.signedInUnmatched);
        fnSetIsAuthenticating(false);
        fnNavigate('/sign-up');
        return;
      }
      const redirect = getRedirectPath(
        oCustomOAuthStateRef.current?.redirect,
        bIsFirstTimeUser.current
      );
      fnNavigate(redirect);
    } else if (oIsUserSignedInRef.current) {
      // Fallback: If we have sign-in but no OAuth state after 2 seconds, proceed anyway
      redirectTimeoutRef.current = setTimeout(() => {
        console.warn('OAuth state timeout - proceeding with redirect fallback');
        if (bIsFirstTimeUser.current) {
          setUserAuthStatus(userAuthStatusMap.signedInUnmatched);
          fnSetIsAuthenticating(false);
          console.log('Navigating to sign-up due to first time user');
          fnNavigate('/sign-up');
        } else {
          const redirect = getRedirectPath(null, bIsFirstTimeUser.current);
          fnNavigate(redirect);
        }
      }, 2000);
    }
  }, [fnNavigate]);

  const fnHandlePostSignOutCleanup = () => {
    fnSetUser(null);
    setUserAuthStatus(userAuthStatusMap.notSignedIn);
    oOAuthStateReceivedRef.current = false;
    oIsUserSignedInRef.current = false;
    oCustomOAuthStateRef.current = null;
    bIsFirstTimeUser.current = false;

    // Clear any pending redirect timeout
    clearTimeoutRef(redirectTimeoutRef);
  };

  useEffect(() => {
    const fnUpdateUser = async (oAuthData) => {
      const sEvent = oAuthData.payload.event;
      try {
        if (sEvent === 'wfuForceTokenRefresh') {
          await fetchAuthSession({ forceRefresh: true }); // Refreshes the Cognito ID token with the newly added DAD ID attribute
        }

        const oCognitoUser = await getCurrentUser();
        const oUserAttributes = await fetchUserAttributes();
        bIsFirstTimeUser.current = isFirstTimeUser(oUserAttributes);
        const sIdp = determineIdpFromCognitoUsername(oCognitoUser.username);
        switch (sEvent) {
          // Page load - user could already be signed in
          case 'wfuInitialAuth':
            if (bIsFirstTimeUser.current) {
              setUserAuthStatus(userAuthStatusMap.signedInUnmatched);
              // Only redirect to /sign-up if not already in the sign-up flow
              if (!oLocation.pathname.startsWith('/sign-up')) {
                fnNavigate('/sign-up');
              }
            } else {
              handleInitialAuth(oCognitoUser);
            }
            break;

          // User signed in
          case 'signInWithRedirect': {
            oIsUserSignedInRef.current = true;

            // For social sign-ins, we might not get customOAuthState, so check URL params
            const urlParams = new URLSearchParams(window.location.search);
            const state = urlParams.get('state');
            if (state) {
              try {
                const decodedState = JSON.parse(decodeURIComponent(state));
                oCustomOAuthStateRef.current = decodedState;
                oOAuthStateReceivedRef.current = true;
              } catch (e) {
                console.warn('Failed to parse OAuth state:', e);
                oOAuthStateReceivedRef.current = true; // Proceed anyway
              }
            } else {
              oOAuthStateReceivedRef.current = true; // Proceed anyway for social sign-ins
            }

            fnSetUser(oCognitoUser);
            // Check if user is in sign-up flow regardless of isFirstTimeUser status
            if (oLocation.pathname.startsWith('/sign-up')) {
              setUserAuthStatus(userAuthStatusMap.signedInUnmatched);
            } else if (!bIsFirstTimeUser.current) {
              setUserAuthStatus(userAuthStatusMap.signedInMatched);
              gtag('event', 'login', {
                method: sIdp,
              });
            }
            fnHandlePostSignInRedirect(); // Avoids race condition with custom OAuth state handling
            break;
          }

          // Handle redirect after successful sign in
          case 'customOAuthState': {
            oOAuthStateReceivedRef.current = true;
            const oCustomState = JSON.parse(oAuthData.payload.data);
            oCustomOAuthStateRef.current = oCustomState;
            fnHandlePostSignInRedirect(); // Avoids race condition with sign in event
            break;
          }

          // User successfully completed the sign up process
          case 'wfuForceTokenRefresh':
            fnSetUser(oCognitoUser);
            setUserAuthStatus(userAuthStatusMap.signedInMatched);
            gtag('event', 'sign_up', {
              method: sIdp,
            });
            fnNavigate('/main');
            break;

          case 'signedOut':
            fnHandlePostSignOutCleanup();
            break;

          default:
            break;
        }
      } catch (error) {
        console.error('Error processing Amplify Hub event:', error);

        if (error.name === 'UserUnAuthenticatedException') {
          setUserAuthStatus(userAuthStatusMap.notSignedIn);
          console.warn('The user is not authenticated.', error);
        } else if (
          sEvent === 'signInWithRedirect' ||
          sEvent === 'wfuInitialAuth'
        ) {
          // For auth-related errors, set appropriate fallback state
          console.warn(
            'Auth error occurred, falling back to not-signed-in state'
          );
          setUserAuthStatus(userAuthStatusMap.notSignedIn);
        }
      } finally {
        // Only set authenticating to false after we've processed the auth event properly
        // Don't set to false if we're still waiting for OAuth state or user is being redirected
        if (sEvent !== 'signInWithRedirect' || !bIsFirstTimeUser.current) {
          fnSetIsAuthenticating(false);
        }
      }
    };

    // Subscribe to AWS Amplify auth events
    const fnUnSubscribeAuth = Hub.listen('auth', fnUpdateUser);

    // Subscribe to custom WFU events
    const fnUnSubscribeWfu = Hub.listen('wfu', fnUpdateUser);

    // Check if the user is already logged in when the page loads,
    // because we won't get a signIn event.
    Hub.dispatch('wfu', {
      event: 'wfuInitialAuth',
      data: {},
      message: '',
    });

    return () => {
      // Unsubscribe from the auth and WFU events when the component unmounts
      fnUnSubscribeAuth();
      fnUnSubscribeWfu();

      // Clear any pending redirect timeout
      clearTimeoutRef(redirectTimeoutRef);
    };
  }, [
    fnHandlePostSignInRedirect,
    fnNavigate,
    oLocation.pathname,
    handleInitialAuth,
  ]);

  const oProviderValue = useMemo(
    () => ({
      userAuthStatus,
    }),
    [userAuthStatus]
  );

  return (
    <LoadingZone isLoading={bIsAuthenticating}>
      <UserAuthContext.Provider value={oProviderValue}>
        {children({ oUser })}
      </UserAuthContext.Provider>
    </LoadingZone>
  );
};

UserAuthManager.propTypes = {
  children: fnChildrenPropType.isRequired,
};

export default UserAuthManager;
